import React, {useState, useEffect, useContext} from 'react';
import {useNavigate} from 'react-router-dom';
import styles from './Dashboard.module.css';
import {PRISMAX_BACKEND_URL} from '../../configs/config';
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import pointsSvg from '../../static_files/point icon.svg';
import fleetbg from "../../static_files/viewFleetBackground.svg";
import robots from "../../static_files/dashboardRobots.png";
import robotsDesktop from "../../static_files/robotsDashboardDesktop.svg";
import {useIsMobile} from "../../utils/utils";

const Dashboard = () => {
    const navigate = useNavigate();
    const [overallPoints, setOverallPoints] = useState(0);
    const [todayPoints, setTodayPoints] = useState(0);

    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const isMobile = useIsMobile(768)
    const isSmallMobile = useIsMobile(420)

    const {
        userEmail,
        walletAddress,
        showFirstTimeModal,
        pointsRefreshTrigger,
        gatewayToken
    } = useContext(SwapMainAppContext);
    useEffect(() => {
        if (walletAddress || userEmail) {
            fetchPointsData();
        } else {
            setOverallPoints(0);
            setTodayPoints(0);
            setChartData([]);
        }
    }, [walletAddress, userEmail, showFirstTimeModal, pointsRefreshTrigger]);
    const fetchPointsData = async () => {
        try {
            let queryParam = '';
            if (userEmail) {
                queryParam = `?email=${userEmail}`;
            } else if (walletAddress) {
                queryParam = `?wallet_address=${walletAddress}`;
            }


            const transResponse = await fetch(`${PRISMAX_BACKEND_URL}/api/get-point-transactions${queryParam}`);

            if (transResponse.ok) {
                const transData = await transResponse.json();
                if (transData.success && transData.data) {
                    if (typeof transData.total_points !== 'undefined') {
                        const points = Math.floor(transData.total_points || 0);
                        setOverallPoints(points.toLocaleString());
                    }
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                    const todayTransactions = transData.data.filter(trans => {
                        const transDate = new Date(trans.user_local_date);
                        const transDateStr = transDate.toISOString().split('T')[0];
                        return transDateStr === todayStr && trans.points_change > 0;
                    });

                    const todayTotal = todayTransactions.reduce((sum, trans) => sum + trans.points_change, 0);
                    setTodayPoints(Math.floor(todayTotal));

                    prepareChartData(transData.data);
                }
            }
        } catch (error) {
            console.error('Error fetching points data:', error);
        } finally {
            setLoading(false);
        }
    };

    const prepareChartData = (transactions) => {
        const days = [];

        const cutNumber = isSmallMobile ? 6 : 12
        for (let i = cutNumber; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            date.setHours(0, 0, 0, 0);
            const localDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            days.push(localDateStr);
        }


        const dailyPoints = days.map(day => {
            const dayTransactions = transactions.filter(trans => {
                const transDate = new Date(trans.user_local_date);
                const transDateStr = transDate.toISOString().split('T')[0];
                return transDateStr === day && trans.points_change > 0;
            });

            const total = dayTransactions.reduce((sum, trans) => sum + trans.points_change, 0);

            const [year, month, dayStr] = day.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(dayStr));
            const monthShort = date.toLocaleDateString('en-US', {month: 'short'});
            const dayNum = date.getDate();

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayLocalStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const isToday = day === todayLocalStr;

            return {
                date: day,
                points: total,
                label: `${dayNum}\n${monthShort}`,
                displayDate: `${monthShort} ${dayNum}`,
                isToday: isToday
            };
        });
        setChartData(dailyPoints);
    };

    const handleViewFleet = () => {
        navigate('/fleet');
    };

    const maxPoints = Math.max(...chartData.map(d => d.points), 1);

    return (
        <div className={styles.container}>
            {/* Top row*/}
            <div className={styles.row}>
                <div className={styles.box} style={{padding: '16px'}}>
                    <div className={styles.dataContent}>
                        <span className={styles.boxTitle}>Overall Prisma Points Earnings</span>
                        <div className={styles.pointsDisplay}>
                            <span className={styles.pointsValue}>{loading ? '...' : overallPoints}</span>
                            <img src={pointsSvg} alt='points'/>
                        </div>
                    </div>
                </div>

                <div className={styles.box} style={{padding: '16px'}}>
                    <div className={styles.dataContent}>
                        <span className={styles.boxTitle}>Today's Prisma Points Earnings</span>
                        <div className={styles.pointsDisplay}>
                            <span className={styles.pointsValue}>{loading ? '...' : todayPoints}</span>
                            <img src={pointsSvg} alt='points'/>
                        </div>
                    </div>
                </div>
                <div className={styles.box} style={{padding: '16px'}}>
                    <div className={styles.dataContent}>
                        <h3 className={styles.dataTitle}>Total hours of data contributed</h3>
                        <div className={styles.dataValue}>0</div>
                    </div>
                </div>
            </div>

            {/* Middle row - two boxes */}
            <div className={styles.row}>
                <div className={`${styles.box} ${styles.reserveBox}`}>
                    <div className={styles.reserveContent}
                         style={{flexDirection: 'row', justifyContent: 'space-between'}}>
                        <div className={styles.reserveContentInner} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start'
                        }}><h3
                            className={styles.reserveTitle}>Reserve your robot today</h3>
                            <button className={styles.upgradeButton} style={{backgroundImage: `url(${fleetbg})`}}
                                    onClick={handleViewFleet}>
                                View our fleet →
                            </button>

                        </div>
                        {
                            isMobile ? <img className={styles.robotsImg} src={robots} alt='robots'/>
                                : <img className={styles.robotsImg} src={robotsDesktop} alt='robots'/>
                        }

                    </div>

                </div>


            </div>

            {/* Bottom row - one large box */}
            <div className={styles.fullWidthBox}>
                <h3 className={styles.chartTitle}>Prisma Points Earnings</h3>
                <div className={styles.chartContainer}>
                    {chartData.map((data, index) => (
                        <div key={index} className={styles.chartBar}>
                            <div className={styles.barWrapper}>
                                <div
                                    className={styles.bar}
                                    style={{height: `${(data.points / maxPoints) * 100}%`}}
                                >
                                    <div className={styles.pointDot}/>
                                    <div className={styles.tooltip}>
                                        <div>{data.displayDate}</div>
                                        <div>{data.points} points</div>
                                    </div>
                                </div>
                            </div>
                            <div className={styles.barLabel}>{data.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;